import type {
  EventModel,
  UserInputContentList,
  UserInputFilePart,
  UserInputFilePartWithUrl,
  UserInputImagePart,
  UserInputImagePartWithUrl,
  UserInputTextPart,
} from "@/api/generated/models";
import { captureMessage } from "@/sentry";
import { AssistantMessageTypes } from "@/types";

type UserInputContentListItem =
  | UserInputTextPart
  | UserInputImagePart
  | UserInputImagePartWithUrl
  | UserInputFilePart
  | UserInputFilePartWithUrl;

/**
 * Get a key used to match client and server user messages.
 * @param message - The user message to find the key.
 * @returns The key for matching the user message.
 */
export const getUserMessageMatchKey = (content: UserInputContentList) => {
  const firstItem = content[0];

  if (!firstItem) {
    return "";
  }

  switch (firstItem.type) {
    case "input_text":
      return firstItem.text ?? "";
    case "input_image":
      return firstItem.attachment_id ?? "";
    case "input_image_with_url":
      return firstItem.url ?? "";
    case "input_file":
      return firstItem.attachment_id ?? "";
    case "input_file_with_url":
      return firstItem.url ?? "";
    default:
      captureMessage(
        "Unsupported content type found while getting user message match key",
        {
          extra: { content },
        },
      );
      const { type: _, ...rest } = firstItem as UserInputContentListItem;
      return JSON.stringify(rest);
  }
};

export type UserSentEventModelOutput = EventModel & {
  matchKey: string;
};
export const spliceClientIdFromUserSentEvents = (
  content: UserInputContentList,
  stack: UserSentEventModelOutput[],
) => {
  const contentMatchKey = getUserMessageMatchKey(content);
  const matchIndex = stack.findLastIndex(
    (message) => message.matchKey === contentMatchKey,
  );

  if (matchIndex === -1) {
    return undefined;
  }

  const [matchedEvent] = stack.splice(matchIndex, 1);
  return matchedEvent.id;
};

const BASE_HIDDEN_TYPES = new Set<string>([
  AssistantMessageTypes.RX_QUESTIONNAIRE_START,
  AssistantMessageTypes.RX_QUESTIONNAIRE_END,
  AssistantMessageTypes.RX_RENEWAL_QUESTIONNAIRE_START,
  AssistantMessageTypes.RX_RENEWAL_QUESTIONNAIRE_END,
  AssistantMessageTypes.RX_RENEWAL_QUESTION,
  AssistantMessageTypes.UPSELL,
  AssistantMessageTypes.CURAI_REDIRECT,
]);

/**
 * Check if a message type should be hidden from rendering.
 *
 * Legacy rx flows (flag-off, the default) hide the question cards and render the consent as a stream
 * button, exactly as before. The clinical-ai-v1-rx generalist flow instead renders the Q&A as a
 * transcript, so it shows the question cards and hides the consent (driven by the inline panel).
 * @param type - The message type to check.
 * @param isGeneralistRx - Whether this conversation is the clinical-ai-v1-rx generalist rx flow.
 * @returns True if the message should be hidden.
 */
export const isHiddenMessage = (
  type: string | null | undefined,
  isGeneralistRx = false,
): boolean => {
  if (type && BASE_HIDDEN_TYPES.has(type)) return true;
  if (isGeneralistRx) {
    // Transcript mode: questions render; the consent + its live question are handled by the panel.
    return type === AssistantMessageTypes.RX_FLOW_CONSENT;
  }
  // Legacy mode: the quiz lives in the Sheet, so its questions stay hidden and consent shows a button.
  return (
    type === AssistantMessageTypes.RX_QUESTION ||
    type === AssistantMessageTypes.LOCATION_QUESTION
  );
};
